import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

export interface ExhibitionEvent {
  id: string;
  name: string;
  description: string | null;
  starts_at: string;
  ends_at: string;
  is_live: boolean;
  countdown_seconds: number;
}

export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  user_name: string;
  user_photo: string | null;
  booth_id: string | null;
  booth_name: string | null;
  score: number;
}

export interface ExhibitionBadge {
  id: string;
  user_id: string;
  event_id: string | null;
  badge_type: string;
  booth_id: string | null;
  booth_name: string | null;
  city_name: string | null;
  country_name: string | null;
  rank: number | null;
  earned_at: string;
}

export const useExhibitionEvents = () => {
  const { user } = useAuthContext();
  const [currentEvent, setCurrentEvent] = useState<ExhibitionEvent | null>(null);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [mostRequestedBooths, setMostRequestedBooths] = useState<LeaderboardEntry[]>([]);
  const [topHosts, setTopHosts] = useState<LeaderboardEntry[]>([]);
  const [popularExhibitors, setPopularExhibitors] = useState<LeaderboardEntry[]>([]);
  const [myBadges, setMyBadges] = useState<ExhibitionBadge[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch current or upcoming event
  const fetchCurrentEvent = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .rpc("get_current_exhibition_event");

      if (!error && data && data.length > 0) {
        const event = data[0];
        setCurrentEvent({
          id: event.id,
          name: event.name,
          description: event.description,
          starts_at: event.starts_at,
          ends_at: event.ends_at,
          is_live: event.is_live,
          countdown_seconds: event.countdown_seconds,
        });
        setCountdownSeconds(event.countdown_seconds);
      } else {
        setCurrentEvent(null);
      }
    } catch (error) {
      console.error("Error fetching current event:", error);
    }
  }, []);

  // Fetch leaderboard by type
  const fetchLeaderboard = useCallback(async (type: string): Promise<LeaderboardEntry[]> => {
    try {
      const { data, error } = await supabase
        .rpc("get_exhibition_leaderboard", { 
          leaderboard_type_param: type,
          limit_count: 10 
        });

      if (error) throw error;
      return (data || []).map((entry: any) => ({
        rank: entry.rank,
        user_id: entry.user_id,
        user_name: entry.user_name || "Anonymous",
        user_photo: entry.user_photo,
        booth_id: entry.booth_id,
        booth_name: entry.booth_name,
        score: entry.score,
      }));
    } catch (error) {
      console.error(`Error fetching ${type} leaderboard:`, error);
      return [];
    }
  }, []);

  // Fetch all leaderboards
  const fetchAllLeaderboards = useCallback(async () => {
    setLoading(true);
    const [mostRequested, hosts, exhibitors] = await Promise.all([
      fetchLeaderboard("most_requested"),
      fetchLeaderboard("top_host"),
      fetchLeaderboard("popular_exhibitor"),
    ]);
    setMostRequestedBooths(mostRequested);
    setTopHosts(hosts);
    setPopularExhibitors(exhibitors);
    setLoading(false);
  }, [fetchLeaderboard]);

  // Fetch user's badges
  const fetchMyBadges = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("exhibition_badges")
        .select("*")
        .eq("user_id", user.id)
        .order("earned_at", { ascending: false });

      if (!error && data) {
        setMyBadges(data);
      }
    } catch (error) {
      console.error("Error fetching badges:", error);
    }
  }, [user]);

  // Trigger leaderboard calculation (admin only)
  const calculateLeaderboard = useCallback(async (eventId?: string) => {
    try {
      const { error } = await supabase
        .rpc("calculate_exhibition_leaderboard", { 
          event_uuid: eventId || null 
        });
      
      if (error) throw error;
      await fetchAllLeaderboards();
    } catch (error) {
      console.error("Error calculating leaderboard:", error);
    }
  }, [fetchAllLeaderboards]);

  // Countdown timer effect
  useEffect(() => {
    if (countdownSeconds <= 0) return;

    const interval = setInterval(() => {
      setCountdownSeconds(prev => {
        if (prev <= 1) {
          // Event is starting, refresh
          fetchCurrentEvent();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdownSeconds, fetchCurrentEvent]);

  // Initial fetch
  useEffect(() => {
    fetchCurrentEvent();
    fetchAllLeaderboards();
    if (user) {
      fetchMyBadges();
    }
  }, [fetchCurrentEvent, fetchAllLeaderboards, fetchMyBadges, user]);

  // Format countdown for display
  const formatCountdown = useCallback((seconds: number) => {
    if (seconds <= 0) return null;
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (days > 0) {
      return { days, hours, minutes, seconds: secs };
    }
    return { days: 0, hours, minutes, seconds: secs };
  }, []);

  return {
    currentEvent,
    countdownSeconds,
    formattedCountdown: formatCountdown(countdownSeconds),
    mostRequestedBooths,
    topHosts,
    popularExhibitors,
    myBadges,
    loading,
    fetchCurrentEvent,
    fetchAllLeaderboards,
    fetchMyBadges,
    calculateLeaderboard,
  };
};
